import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Form, Button, Container, Row, Col, Table, Card, InputGroup, Tabs, Tab, Badge, ProgressBar, Spinner, Modal } from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileInvoice, faBoxOpen, faEuroSign, faTruck, faChartLine, faEdit } from '@fortawesome/free-solid-svg-icons';
import { getCorrieri, getBanche, getClienti, getOrdine, updateOrdine, searchItems, splitOrder, getStatiOrdine, resendConfirmationEmail, ricalcolaPromoOrdine } from '../services/api';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useTornaIndietro } from '../common/navigazione';
import { toast } from 'react-toastify';
import OrderMessages from '../components/OrderMessages';
import MotivoRimborsoFields, { motivoRimborsoCompleto } from '../common/MotivoRimborsoFields';
import RimborsoMiraklInfo from '../common/RimborsoMiraklInfo';
import { METODI_PAGAMENTO, bancaSuggerita, labelMetodoPagamento } from '../common/metodiPagamento';

const MESSAGGI_BADGE_BG = {
  'Risposto': 'success',
  'Da Rispondere': 'danger',
  'Non Presente': 'secondary',
};


const OrdiniEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const tornaIndietro = useTornaIndietro('/ordini');
  const location = useLocation();

  const mapOrigineToCanale = (origine) => {
    switch (origine) {
      case 'NEGOZIO':
        return 'NEGOZIO';
      case 'SITO_WEB':
      case 'SITO WEB': // Supporto retrocompatibilità
        return 'SITO_WEB';
      case 'IBS':
        return 'IBS';
      case 'FELTRINELLI':
        return 'FELTRINELLI';
      case 'RIVERSAMENTI':
        return 'RIVERSAMENTI';
      default:
        return null;
    }
  };

  // State for dropdowns and suggestions
  const [corrieri, setCorrieri] = useState([]);
  const [banche, setBanche] = useState([]);
  const [allClienti, setAllClienti] = useState([]);
  const [searchResults, setSearchResults] = useState([]); // New state for unified search results
  const [selectedDetails, setSelectedDetails] = useState(new Set());
  const [statiOrdine, setStatiOrdine] = useState([]); // Nuovo stato per gli stati ordine
  const [cliente, setCliente] = useState(null); // Stato per i dettagli del cliente
  const [saving, setSaving] = useState(false); // Stato per il salvataggio
  const [showSaveProgress, setShowSaveProgress] = useState(false); // Modal per progress bar


  const handleResendConfirmationEmail = async () => {
    if (window.confirm(`Sei sicuro di voler inviare nuovamente l'email di conferma per l'ordine #${id}?`)) {
        try {
            const response = await resendConfirmationEmail(id);
            toast.success(response.data.message || "Email inviata con successo!");
        } catch (error) {
            console.error("Errore durante il reinvio dell'email:", error);
            toast.error(`Impossibile inviare l'email: ${error.response?.data?.detail || error.message}`);
        }
    }
  };



  // Evita il ricalcolo spese_trasporto al primo caricamento di un ordine esistente
  const speseTrasportoInitialized = useRef(false);

  // Main order state
  const [ordine, setOrdine] = useState({
    cliente_id: '',
    corriere_id: '',
    banca_id: '',
    data_ordine: new Date().toLocaleDateString('sv-SE'),
    spese_trasporto: 0,
    costo_contrassegno: 0,
    spese_incasso_percentuale: 0,
    acconto: 0,
    sconto: 0,
    tipo_sconto: 'fisso', // New field for discount type
    origine: '',
    numero_ordine_marketplace: '',
    note: '',
    dettagli: [],
    subtotale_imponibile: 0,
    totale_iva: 0,
    totale_ordine: 0,
    stato: 'Da Evadere',
    costo_prodotti: 0,
    costo_marketplace: 0,
    costo_reale_corriere: 0,
    margine_lordo: 0,
    margine: 0
  });

  // State for the new line item form
  const [selectedItem, setSelectedItem] = useState(null); // Changed from selectedFilm
  const [quantita, setQuantita] = useState(1);
  const [isCustomProductMode, setIsCustomProductMode] = useState(false);
  const [customProductDescription, setCustomProductDescription] = useState('');
  const [customProductPrice, setCustomProductPrice] = useState(0);
  const [customProductCost, setCustomProductCost] = useState(0); // New state for custom product cost
  const [customProductIva, setCustomProductIva] = useState(22);
  // location.state.initialTab apre direttamente quella tab (es. link dal modale "Concludi con Tracking")
  const [activeTab, setActiveTab] = useState(location.state?.initialTab || 'intestazione'); // Stato per gestire il tab attivo
  const [editingDetail, setEditingDetail] = useState(null); // Stato per gestire la riga in modifica

  // Fetch initial data for dropdowns and order details
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [corrieriRes, bancheRes, clientiRes, statiOrdineRes] = await Promise.all([
            getCorrieri(0, 100000),
            getBanche(0, 100000),
            getClienti(0, 100000),
            getStatiOrdine(), // Recupera gli stati ordine
        ]);
        setCorrieri(corrieriRes.data.corrieri);
        setBanche(bancheRes.data.banche);
        setAllClienti(clientiRes.data.clienti);
        setStatiOrdine(statiOrdineRes.data); // Salva gli stati ordine

        if (id) { // Se c'è un ID, siamo in modalità modifica
            const ordineRes = await getOrdine(id);
            const fetchedOrdine = ordineRes.data;
            setCliente(fetchedOrdine.cliente); // Salva i dettagli del cliente
            speseTrasportoInitialized.current = true; // Skip ricalcolo al primo render
            setOrdine(prevOrdine => {
                const newOrdine = {
                    ...fetchedOrdine,
                    data_ordine: fetchedOrdine.data_ordine.split('T')[0],
                    cliente_id: fetchedOrdine.cliente.id,
                    corriere_id: fetchedOrdine.corriere ? fetchedOrdine.corriere.id : '',
                    banca_id: fetchedOrdine.banca ? fetchedOrdine.banca.id : '',
                    subtotale_imponibile: fetchedOrdine.subtotale_imponibile || 0,
                    totale_iva: fetchedOrdine.totale_iva || 0,
                    totale_ordine: fetchedOrdine.totale_ordine || 0,
                    acconto: fetchedOrdine.acconto || 0,
                    sconto: fetchedOrdine.sconto || 0,
                    tipo_sconto: fetchedOrdine.tipo_sconto || 'fisso',
                    costo_prodotti: fetchedOrdine.costo_prodotti || 0,
                    costo_marketplace: fetchedOrdine.costo_marketplace || 0,
                    costo_reale_corriere: fetchedOrdine.costo_reale_corriere || 0,
                    margine_lordo: fetchedOrdine.margine_lordo || 0,
                    margine: fetchedOrdine.margine || 0,
                    costo_contrassegno: fetchedOrdine.costo_contrassegno || 0,
                    payment_method: fetchedOrdine.payment_method || '', // Assicurati di caricare anche il metodo di pagamento
                    dettagli: fetchedOrdine.dettagli.map(d => {
                        let titolo = '';
                        let prezzo_unitario_display = d.prezzo_unitario;
                        let iva_percentuale_display = d.iva_percentuale;
                        let costo_acquisto_unitario_display = d.costo_acquisto_unitario;

                        if (d.prodotto_libero) {
                            titolo = d.descrizione_prodotto_libero;
                            prezzo_unitario_display = d.prezzo_unitario_libero;
                            iva_percentuale_display = d.iva_percentuale_libero;
                            costo_acquisto_unitario_display = d.costo_unitario_libero;
                        } else if (d.film) {
                            titolo = d.film.titolo;
                        } else if (d.musica) {
                            titolo = d.musica.titolo;
                        } else if (d.collectible) {
                            titolo = d.collectible.nome;
                        } else if (d.servizio) {
                            titolo = d.servizio.titolo_servizio;
                        }
                        return {
                            ...d,
                            titolo: titolo,
                            prezzo_unitario: prezzo_unitario_display,
                            iva_percentuale: iva_percentuale_display,
                            costo_acquisto_unitario: costo_acquisto_unitario_display
                        };
                    })
                };
                return newOrdine;
            });
        } else { // Siamo in modalità creazione, inizializza con valori di default
            setOrdine(prevOrdine => ({
                ...prevOrdine,
                data_ordine: new Date().toLocaleDateString('sv-SE'),
                stato: 'Da Evadere',
                sconto: 0,
                tipo_sconto: 'fisso',
                spese_trasporto: 0,
                costo_contrassegno: 0,
                spese_incasso_percentuale: 0,
                acconto: 0,
                note: '',
                subtotale_imponibile: 0,
                totale_iva: 0,
                totale_ordine: 0,
                costo_prodotti: 0,
                costo_marketplace: 0,
                costo_reale_corriere: 0,
                margine_lordo: 0,
                margine: 0,
                payment_method: '', // Inizializza anche il metodo di pagamento
                dettagli: []
            }));
        }

      } catch (error) {
        console.error("Errore nel caricamento dei dati", error);
      }
    };
    fetchData();
  }, [id]);

  useEffect(() => {
  
  }, [ordine]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setOrdine(prevOrdine => ({
      ...prevOrdine,
      [name]: value,
    }));
  }, []);

  const handleBancaChange = useCallback((e) => {
    const bancaId = e.target.value;
    const selectedBanca = banche.find(b => b.id === parseInt(bancaId));
    setOrdine(prev => ({
      ...prev,
      banca_id: bancaId ? parseInt(bancaId) : null,
      spese_incasso_percentuale: selectedBanca ? selectedBanca.costo_transazione_percentuale || 0 : 0
    }));
  }, [banche]);

  // Il metodo suggerisce la banca solo se l'ordine non ne ha ancora una
  const handlePaymentMethodChange = useCallback((e) => {
    const metodo = e.target.value;
    setOrdine(prev => {
      const next = { ...prev, payment_method: metodo };
      if (!prev.banca_id) {
        const suggerita = bancaSuggerita(metodo, banche);
        if (suggerita) {
          next.banca_id = suggerita.id;
          next.spese_incasso_percentuale = suggerita.costo_transazione_percentuale || 0;
        }
      }
      return next;
    });
  }, [banche]);

  const handleClienteChange = useCallback((selected) => {
    if (selected.length > 0) {
      setOrdine(prevOrdine => ({ ...prevOrdine, cliente_id: selected[0].id }));
    } else {
      setOrdine(prevOrdine => ({ ...prevOrdine, cliente_id: '' }));
    }
  }, []);

  // General change handler for order header
  const handleOrdineChange = useCallback((e) => {
    const { name, value } = e.target;

    // These fields are expected to be numbers in the backend schema
    const numericFields = ['spese_incasso_percentuale', 'sconto', 'acconto'];
    let finalValue = value;

    if (numericFields.includes(name)) {
        // Parse the value as a float. If empty string, use 0. If NaN, use 0.
        finalValue = value === '' ? 0 : parseFloat(value);
        if (isNaN(finalValue)) {
            finalValue = 0;
        }
    }
    
    setOrdine(prev => ({ ...prev, [name]: finalValue }));
}, []); // Rimosso il calcolo delle spese_trasporto qui, sarà gestito dall'useEffect dedicato

  // useEffect per ricalcolare le spese di trasporto quando cambiano corriere o origine
  useEffect(() => {
    // Salta il ricalcolo al primo caricamento di un ordine esistente: preserva il valore importato
    if (speseTrasportoInitialized.current) {
      speseTrasportoInitialized.current = false;
      return;
    }

    let calculatedSpeseTrasporto = 0;
    const selectedCorriere = corrieri.find(c => c.id === parseInt(ordine.corriere_id));
    // Usa lo stato 'cliente' (dati completi dall'ordine) come sorgente primaria,
    // poi fallback su allClienti (lista ridotta dei dropdown)
    const selectedCliente = (cliente && cliente.id === parseInt(ordine.cliente_id))
      ? cliente
      : allClienti.find(c => c.id === parseInt(ordine.cliente_id));

    console.log('🔍 DEBUG SPESE TRASPORTO - OrdiniEdit');
    console.log('Corriere selezionato:', selectedCorriere);
    console.log('Regole spedizione:', selectedCorriere?.regole_spedizione);
    console.log('Cliente selezionato:', selectedCliente);
    console.log('Nazione cliente (originale):', selectedCliente?.nazione);
    console.log('Origine ordine:', ordine.origine);

    if (selectedCorriere && selectedCorriere.regole_spedizione && selectedCorriere.regole_spedizione.length > 0) {
      const targetCanale = mapOrigineToCanale(ordine.origine);
      console.log('Target canale:', targetCanale);

      if (targetCanale) {
        // Determina il codice_logica basato sul paese del cliente
        let codiceLogica = 'STANDARD_ITA'; // Default per Italia
        
        if (selectedCliente && selectedCliente.nazione) {
          const nazione = selectedCliente.nazione.toUpperCase().trim();
          console.log('Nazione cliente (uppercase):', nazione);
          if (nazione === 'SVIZZERA' || nazione === 'SWITZERLAND' || nazione === 'CH') {
            codiceLogica = 'SVIZZERA';
          } else if (nazione === 'ITALIA' || nazione === 'ITALY' || nazione === 'IT' || nazione === '') {
            // Per l'Italia, determina la logica basata sul CAP
            if (selectedCliente.cap) {
              const cap = selectedCliente.cap.trim();
              if (cap.length === 5 && cap.match(/^\d{5}$/)) {
                const capPrefix = parseInt(cap.substring(0, 2));
                const MODENA_CAPS = ['41121', '41122', '41123', '41124', '41125', '41126'];
                
                if (MODENA_CAPS.includes(cap)) {
                  codiceLogica = 'MODENA';
                } else if (capPrefix >= 87 && capPrefix <= 89) { // Calabria
                  codiceLogica = 'ISOLE_SUD';
                } else if (capPrefix >= 90 && capPrefix <= 98) { // Sicilia
                  codiceLogica = 'ISOLE_SUD';
                } else if ([7, 8, 9].includes(capPrefix)) { // Sardegna
                  codiceLogica = 'ISOLE_SUD';
                } else {
                  codiceLogica = 'STANDARD_ITA';
                }
              }
            }
          } else {
            // Mappa paese → codice_logica specifico (come da CodiceLogicaSpedizioneEnum)
            // con fallback a UE generico o EXTRA_UE
            const mappazioneNazioneCodice = {
              'AUSTRIA': 'AUSTRIA', 'AT': 'AUSTRIA',
              'BELGIO': 'BELGIO', 'BE': 'BELGIO', 'BELGIUM': 'BELGIO',
              'BULGARIA': 'BULGARIA', 'BG': 'BULGARIA',
              'CIPRO': 'CIPRO', 'CY': 'CIPRO', 'CYPRUS': 'CIPRO',
              'CROAZIA': 'CROAZIA', 'HR': 'CROAZIA', 'CROATIA': 'CROAZIA',
              'DANIMARCA': 'DANIMARCA', 'DK': 'DANIMARCA', 'DENMARK': 'DANIMARCA',
              'ESTONIA': 'ESTONIA', 'EE': 'ESTONIA',
              'FINLANDIA': 'FINLANDIA', 'FI': 'FINLANDIA', 'FINLAND': 'FINLANDIA',
              'FRANCIA': 'FRANCIA', 'FR': 'FRANCIA', 'FRANCE': 'FRANCIA',
              'GERMANIA': 'GERMANIA', 'DE': 'GERMANIA', 'GERMANY': 'GERMANIA',
              'GRECIA': 'GRECIA', 'GR': 'GRECIA', 'GREECE': 'GRECIA',
              'IRLANDA': 'IRLANDA', 'IE': 'IRLANDA', 'IRELAND': 'IRLANDA',
              'LETTONIA': 'LETTONIA', 'LV': 'LETTONIA', 'LATVIA': 'LETTONIA',
              'LITUANIA': 'LITUANIA', 'LT': 'LITUANIA', 'LITHUANIA': 'LITUANIA',
              'LUSSEMBURGO': 'LUSSEMBURGO', 'LU': 'LUSSEMBURGO', 'LUXEMBOURG': 'LUSSEMBURGO',
              'MALTA': 'MALTA', 'MT': 'MALTA',
              'PAESI BASSI': 'PAESI_BASSI', 'NL': 'PAESI_BASSI', 'NETHERLANDS': 'PAESI_BASSI', 'OLANDA': 'PAESI_BASSI',
              'POLONIA': 'POLONIA', 'PL': 'POLONIA', 'POLAND': 'POLONIA',
              'PORTOGALLO': 'PORTOGALLO', 'PT': 'PORTOGALLO', 'PORTUGAL': 'PORTOGALLO',
              'REPUBBLICA CECA': 'REPUBBLICA_CECA', 'CZ': 'REPUBBLICA_CECA', 'CZECH REPUBLIC': 'REPUBBLICA_CECA',
              'ROMANIA': 'ROMANIA', 'RO': 'ROMANIA',
              'SLOVACCHIA': 'SLOVACCHIA', 'SK': 'SLOVACCHIA', 'SLOVAKIA': 'SLOVACCHIA',
              'SLOVENIA': 'SLOVENIA', 'SI': 'SLOVENIA',
              'SPAGNA': 'SPAGNA', 'ES': 'SPAGNA', 'SPAIN': 'SPAGNA',
              'SVEZIA': 'SVEZIA', 'SE': 'SVEZIA', 'SWEDEN': 'SVEZIA',
              'UNGHERIA': 'UNGHERIA', 'HU': 'UNGHERIA', 'HUNGARY': 'UNGHERIA',
              'NORVEGIA': 'NORVEGIA', 'NO': 'NORVEGIA', 'NORWAY': 'NORVEGIA',
              'ISLANDA': 'ISLANDA', 'IS': 'ISLANDA', 'ICELAND': 'ISLANDA',
              // UE generico
              'UE': 'UE', 'EU': 'UE',
            };

            if (mappazioneNazioneCodice[nazione]) {
              codiceLogica = mappazioneNazioneCodice[nazione];
            } else {
              codiceLogica = 'EXTRA_UE';
            }
          }
        }

        console.log('Codice logica determinato:', codiceLogica);

        // Cerca una regola che corrisponde sia al canale che al codice_logica
        // Normalizziamo entrambi a maiuscolo per evitare problemi di case
        let applicableRule = selectedCorriere.regole_spedizione.find(rule => {
          const ruleCanale = rule.canale ? rule.canale.toUpperCase() : '';
          const ruleLogica = rule.codice_logica ? rule.codice_logica.toUpperCase() : '';
          console.log(`Confronto regola: canale="${ruleCanale}" vs "${targetCanale}", logica="${ruleLogica}" vs "${codiceLogica}"`);
          return ruleCanale === targetCanale && ruleLogica === codiceLogica;
        });

        // Fallback: se l'origine è NEGOZIO e non trova una regola NEGOZIO,
        // prova con SITO_WEB (stessa logica di calcolo)
        if (!applicableRule && targetCanale === 'NEGOZIO') {
          console.log('🔄 Fallback: provo canale SITO_WEB per origine NEGOZIO');
          applicableRule = selectedCorriere.regole_spedizione.find(rule => {
            const ruleCanale = rule.canale ? rule.canale.toUpperCase() : '';
            const ruleLogica = rule.codice_logica ? rule.codice_logica.toUpperCase() : '';
            return ruleCanale === 'SITO_WEB' && ruleLogica === codiceLogica;
          });
        }

        // Fallback 2: se non trova una regola paese-specifica, prova il codice UE generico
        if (!applicableRule && codiceLogica !== 'UE' && codiceLogica !== 'EXTRA_UE' && codiceLogica !== 'STANDARD_ITA') {
          const fallbackCanale = targetCanale === 'NEGOZIO' ? 'SITO_WEB' : targetCanale;
          console.log(`🔄 Fallback 2: provo ${fallbackCanale} + UE (nessuna regola ${codiceLogica} trovata)`);
          applicableRule = selectedCorriere.regole_spedizione.find(rule => {
            const ruleCanale = rule.canale ? rule.canale.toUpperCase() : '';
            const ruleLogica = rule.codice_logica ? rule.codice_logica.toUpperCase() : '';
            return ruleCanale === fallbackCanale && ruleLogica === 'UE';
          });
        }

        // Fallback 3: se ancora non trova una regola (es. nessuna UE), prova STANDARD_ITA
        if (!applicableRule && codiceLogica !== 'STANDARD_ITA') {
          const fallbackCanale = targetCanale === 'NEGOZIO' ? 'SITO_WEB' : targetCanale;
          console.log(`🔄 Fallback 3: provo ${fallbackCanale} + STANDARD_ITA (nessuna regola ${codiceLogica}/UE trovata)`);
          applicableRule = selectedCorriere.regole_spedizione.find(rule => {
            const ruleCanale = rule.canale ? rule.canale.toUpperCase() : '';
            const ruleLogica = rule.codice_logica ? rule.codice_logica.toUpperCase() : '';
            return ruleCanale === fallbackCanale && ruleLogica === 'STANDARD_ITA';
          });
        }

        console.log('Regola applicabile trovata:', applicableRule);

        if (applicableRule) {
          calculatedSpeseTrasporto = applicableRule.costo;
          console.log('✅ Costo calcolato da regola:', calculatedSpeseTrasporto);
        } else if (selectedCorriere.costo && selectedCorriere.costo > 0) {
          // Fallback 3: usa il costo base del corriere se non c'è nessuna regola applicabile
          calculatedSpeseTrasporto = selectedCorriere.costo;
          console.log('✅ Costo calcolato da corriere.costo (fallback):', calculatedSpeseTrasporto);
        } else {
          console.log('❌ Nessuna regola trovata per canale:', targetCanale, 'e logica:', codiceLogica);
        }
      }
    } else {
      console.log('⚠️ Corriere non ha regole di spedizione o corriere non selezionato');
    }

    console.log('Spese trasporto finale:', calculatedSpeseTrasporto);

    if (parseFloat(ordine.spese_trasporto) !== calculatedSpeseTrasporto) {
      setOrdine(prev => ({ ...prev, spese_trasporto: calculatedSpeseTrasporto }));
    }

  }, [ordine.corriere_id, ordine.origine, ordine.cliente_id, corrieri, allClienti, cliente, mapOrigineToCanale]);
  
    // Sconto promozionale e spedizione che applicherebbe il sito agli articoli attuali (salvati: prima salva l'ordine)
    const handleRicalcolaPromo = async () => {
      try {
        const { data } = await ricalcolaPromoOrdine(id);
        const euro = (v) => `€ ${Number(v || 0).toFixed(2)}`;
        const righe = [
          `Promozioni: ${data.promozioni.length ? data.promozioni.join(', ') : 'nessuna'}`,
          `Sconto: ${euro(data.sconto)} (adesso ${euro(ordine.sconto)})`,
          `Spedizione: ${data.spedizione_gratuita ? 'gratuita' : euro(data.spese_trasporto)} (adesso ${euro(ordine.spese_trasporto)})`,
        ];
        if (!data.spedizione_gratuita && data.mancano_per_spedizione_gratuita) righe.push(`Mancano ${euro(data.mancano_per_spedizione_gratuita)} per la spedizione gratuita.`);
        if (data.righe_libere_ignorate) righe.push(`${data.righe_libere_ignorate} righe libere non entrano nel calcolo.`);
        if (data.codice_sconto_presente) righe.push('Attenzione: l’ordine ha un codice sconto, non incluso in questo calcolo.');
        if (!window.confirm(`Il sito applicherebbe:\n\n${righe.join('\n')}\n\nApplicare questi valori all’ordine?`)) return;
        setOrdine(prev => ({ ...prev, sconto: data.sconto, tipo_sconto: 'fisso', spese_trasporto: data.spese_trasporto }));
      } catch (error) {
        alert(error?.response?.data?.detail || 'Impossibile ricalcolare le promozioni.');
      }
    };

    // Handle search for items (Film, Musica, Servizi)
    const handleSearch = useCallback(async (searchText) => {
      if (searchText.length > 2) {
          try {
              const response = await searchItems({ 
                  query: searchText, 
                  sku: searchText, 
                  barcode: searchText 
              });
              setSearchResults(response.data);
          } catch (error) {
              console.error("Errore durante la ricerca unificata:", error);
              setSearchResults([]);
          }
      } else {
          setSearchResults([]);
      }
  }, []);
  
    // Add/Remove line items
    const addLineItem = () => {
      if (isCustomProductMode) {
          if (!customProductDescription || quantita <= 0 || customProductPrice <= 0) {
              alert("Inserisci descrizione, quantità e prezzo validi per il prodotto personalizzato.");
              return;
          }
          const item = {
              prodotto_libero: true,
              descrizione_prodotto_libero: customProductDescription,
              prezzo_unitario_libero: parseFloat(customProductPrice),
              costo_unitario_libero: parseFloat(customProductCost), // Include custom product cost
              iva_percentuale_libero: parseFloat(customProductIva),
              quantita: parseInt(quantita),
              // For display purposes in the table
              titolo: customProductDescription,
              prezzo_unitario: parseFloat(customProductPrice),
              iva_percentuale: parseFloat(customProductIva)
          };
          setOrdine(prev => ({ ...prev, dettagli: [...prev.dettagli, item] }));
          setCustomProductDescription('');
          setCustomProductPrice(0);
          setCustomProductCost(0); // Reset custom product cost
          setCustomProductIva(22);
          setQuantita(1);
      } else {
          if (!selectedItem) { // selectedItem ora rappresenta l'elemento generico selezionato
              alert("Seleziona un prodotto o servizio.");
              return;
          }
          if (quantita < 0) {
              alert("La quantità non può essere negativa.");
              return;
          }
  
          // Warn if quantity exceeds available stock for Film/Musica
          if ((selectedItem.item_type === "Film" || selectedItem.item_type === "Musica") && quantita > selectedItem.quantita) {
              if (!window.confirm(`Attenzione: La quantità richiesta (${quantita}) supera la disponibilità (${selectedItem.quantita}) per ${selectedItem.titolo}. Continuare?`)) {
                  return; // User cancelled
              }
          }
  
          const { origine } = ordine;
          const isMarketplace = origine === 'IBS' || origine === 'FELTRINELLI';
  
          let prezzoUnitario;
          if (isMarketplace && (selectedItem.item_type === 'Film' || selectedItem.item_type === 'Musica') && selectedItem.prezzo_ibs != null) {
              prezzoUnitario = selectedItem.prezzo_ibs;
          } else {
              // Fallback to standard price for non-marketplace orders or if prezzo_ibs is not available
              prezzoUnitario = selectedItem.prezzo_negozio || selectedItem.prezzo || 0;
          }
  
          let itemToAdd = {
              quantita: parseInt(quantita),
              prezzo_unitario: prezzoUnitario,
              iva_percentuale: selectedItem.iva || 22,
              costo_acquisto_unitario: selectedItem.costo_acquisto || selectedItem.costo || 0, // Aggiunto costo
              titolo: selectedItem.titolo, // For display purposes in frontend
              item_type: selectedItem.item_type, // For display purposes in frontend
              film_id: selectedItem.item_type === "Film" ? selectedItem.id : null,
              musica_id: selectedItem.item_type === "Musica" ? selectedItem.id : null,
              servizio_id: selectedItem.item_type === "Servizio" ? selectedItem.id : null,
              collectible_id: selectedItem.item_type === "Collectible" ? selectedItem.id : null,
              quantita_magazzino: selectedItem.quantita,
              prodotto_libero: false
          };
  
          setOrdine(prev => ({ ...prev, dettagli: [...prev.dettagli, itemToAdd] }));
          setSelectedItem(null); // Reset selected item
          setQuantita(1); // Reset quantity
      }
    };
  
    const removeLineItem = (index) => {
      setOrdine(prev => ({ ...prev, dettagli: prev.dettagli.filter((_, i) => i !== index) }));
    };
  
    const handleQuantityChange = (index, newQuantita) => {
      setOrdine(prev => ({
        ...prev,
        dettagli: prev.dettagli.map((item, i) => 
          i === index ? { ...item, quantita: newQuantita } : item
        )
      }));
    };

    const handleDescriptionChange = (index, newDescription) => {
      setOrdine(prev => ({
        ...prev,
        dettagli: prev.dettagli.map((item, i) => 
          i === index ? { 
            ...item, 
            titolo: newDescription,
            descrizione_prodotto_libero: item.prodotto_libero ? newDescription : item.descrizione_prodotto_libero
          } : item
        )
      }));
    };

    const handlePriceChange = (index, newPrice) => {
      setOrdine(prev => ({
        ...prev,
        dettagli: prev.dettagli.map((item, i) => 
          i === index ? {
            ...item,
            prezzo_unitario: parseFloat(newPrice),
            prezzo_unitario_libero: item.prodotto_libero ? parseFloat(newPrice) : item.prezzo_unitario_libero
          } : item
        )
      }));
    };

    const toggleEditDetail = (index) => {
      setEditingDetail(editingDetail === index ? null : index);
    };

    const handleDetailSelection = (detailId) => {
        setSelectedDetails(prev => {
            const newSelection = new Set(prev);
            if (newSelection.has(detailId)) {
                newSelection.delete(detailId);
            } else {
                newSelection.add(detailId);
            }
            return newSelection;
        });
    };

    const handlePartialEvasion = async () => {
        if (selectedDetails.size === 0) {
            toast.warn("Seleziona almeno un prodotto da evadere.");
            return;
        }

        if (selectedDetails.size === ordine.dettagli.length) {
            toast.info("Hai selezionato tutti i prodotti. Per evadere l'intero ordine, cambia semplicemente il suo stato in 'Concluso'.");
            return;
        }

        if (window.confirm(`Sei sicuro di voler evadere ${selectedDetails.size} prodotti creando un nuovo ordine "concluso" e lasciando i restanti in questo ordine come "parzialmente concluso"?`)) {
            try {
                await splitOrder(id, Array.from(selectedDetails));
                toast.success("Ordine diviso con successo! Sarai reindirizzato alla lista ordini.");
                setTimeout(() => navigate('/ordini'), 2000);
            } catch (error) {
                console.error("Errore durante l'evasione parziale:", error);
                toast.error(`Errore durante la divisione dell'ordine: ${error.response?.data?.detail || error.message}`);
            }
        }
    };
  
    const calculateTotal = useCallback(() => {
      let subtotale_imponibile_prodotti = 0;
      let totale_iva_prodotti = 0;
      let costo_prodotti = 0;
      let totale_lordo_prodotti_senza_sconto = 0;
  
      ordine.dettagli.forEach(item => {
          let prezzo_unitario_effettivo = item.prezzo_unitario;
          let iva_percentuale_effettiva = item.iva_percentuale;
          let costo_unitario_effettivo = item.costo_acquisto_unitario || 0;
  
          if (item.prodotto_libero) {
              prezzo_unitario_effettivo = item.prezzo_unitario_libero;
              iva_percentuale_effettiva = item.iva_percentuale_libero;
              costo_unitario_effettivo = item.costo_unitario_libero || 0;
          }
  
          const grossItemTotal = prezzo_unitario_effettivo * item.quantita;
          const ivaRate = iva_percentuale_effettiva / 100;
          
          const netItemTotal = grossItemTotal / (1 + ivaRate);
          const ivaAmount = grossItemTotal - netItemTotal;
  
          subtotale_imponibile_prodotti += netItemTotal;
          totale_iva_prodotti += ivaAmount;
          costo_prodotti += costo_unitario_effettivo * item.quantita;
          totale_lordo_prodotti_senza_sconto += grossItemTotal;
      });
  
      // Stessa formula del backend (_recalculate_order_totals), che ricalcola e salva il totale: prodotti
      // + spedizione + contrassegno, sconto (% su tutto, oppure fisso). Le spese di incasso sono la commissione
      // della banca (costo, non addebitato al cliente) e l'acconto si sottrae solo nel "da incassare".
      let totale = totale_lordo_prodotti_senza_sconto + parseFloat(ordine.spese_trasporto || 0) + parseFloat(ordine.costo_contrassegno || 0);
      const sconto = parseFloat(ordine.sconto || 0);
      if (sconto > 0) {
          totale -= ordine.tipo_sconto === 'percentuale' ? totale * sconto / 100 : sconto;
      }

      const margine_lordo_calcolato = totale - costo_prodotti;
      const margine_calcolato = margine_lordo_calcolato - (ordine.costo_marketplace || 0) - (ordine.costo_reale_corriere || 0);

      setOrdine(prev => ({
          ...prev,
          subtotale_imponibile: subtotale_imponibile_prodotti,
          totale_iva: totale_iva_prodotti,
          totale_ordine: totale,
          costo_prodotti: costo_prodotti,
          margine_lordo: margine_lordo_calcolato,
          margine: totale > 0 ? (margine_calcolato / totale) * 100 : 0  // mostrato come "Margine %"
      }));
    }, [ordine.dettagli, ordine.spese_trasporto, ordine.costo_contrassegno, ordine.spese_incasso_percentuale, ordine.acconto, ordine.sconto, ordine.tipo_sconto, ordine.costo_marketplace, ordine.costo_reale_corriere]);
  
    useEffect(() => {
      if(ordine.dettagli.length > 0 || ordine.spese_trasporto !== 0 || ordine.spese_incasso_percentuale !== 0 || ordine.acconto !== 0 || ordine.sconto !== 0) {
          calculateTotal();
      }
    }, [calculateTotal, ordine.dettagli.length, ordine.spese_trasporto, ordine.costo_contrassegno, ordine.spese_incasso_percentuale, ordine.acconto, ordine.sconto]);
  
    // Form submission
      const handleSubmit = async (e) => {
        e.preventDefault();
        if (!ordine.cliente_id || ordine.dettagli.length === 0) {
            alert("Seleziona un cliente e aggiungi almeno un prodotto all'ordine.");
            return;
        }
        if (ordine.stato === 'Rimborsato' && !motivoRimborsoCompleto(ordine)) {
            alert("Indica il motivo del rimborso (con una nota se scegli \"Altro\").");
            return;
        }
        
        setSaving(true);
        setShowSaveProgress(true);
        
        try {
          const ordineToSubmit = {
              cliente_id: ordine.cliente_id,
              corriere_id: ordine.corriere_id ? parseInt(ordine.corriere_id) : null,
              banca_id: ordine.banca_id ? parseInt(ordine.banca_id) : null,
              data_ordine: ordine.data_ordine,
              spese_trasporto: ordine.spese_trasporto,
              costo_contrassegno: parseFloat(ordine.costo_contrassegno || 0),
              spese_incasso_percentuale: ordine.spese_incasso_percentuale,
              subtotale_imponibile: ordine.subtotale_imponibile,
              totale_iva: ordine.totale_iva,
              totale_ordine: ordine.totale_ordine,
              costo_prodotti: ordine.costo_prodotti,
              costo_marketplace: ordine.costo_marketplace,
              margine_lordo: ordine.margine_lordo,
              margine: ordine.margine,
              acconto: ordine.acconto,
              sconto: ordine.sconto,
              tipo_sconto: ordine.tipo_sconto, // Aggiunto tipo_sconto
              origine: ordine.origine,
              numero_ordine_marketplace: ordine.numero_ordine_marketplace,
              note: ordine.note,
              stato: ordine.stato,
              ...(ordine.stato === 'Rimborsato' ? { motivo_rimborso: ordine.motivo_rimborso, note_rimborso: ordine.note_rimborso || null } : {}),
              tracking_code: ordine.tracking_code,
              payment_method: ordine.payment_method || null,
              dettagli: ordine.dettagli.map(d => {
                  const detail = {
                      id: d.id || undefined,
                      quantita: d.quantita,
                      prezzo_unitario: d.prezzo_unitario,
                      iva_percentuale: d.iva_percentuale,
                      film_id: d.film_id,
                      musica_id: d.musica_id,
                      servizio_id: d.servizio_id,
                      collectible_id: d.collectible_id, // Aggiunto collectible_id
                      prodotto_libero: d.prodotto_libero || false,
                      descrizione_prodotto_libero: d.descrizione_prodotto_libero || null,
                      prezzo_unitario_libero: d.prezzo_unitario_libero || null,
                      costo_unitario_libero: d.costo_unitario_libero || null,
                      iva_percentuale_libero: d.iva_percentuale_libero || null,
                      // Costo d'acquisto delle righe aggiunte qui (senza, margine e costo del venduto risultano gonfiati);
                      // omesso se non noto, cosi' il backend non azzera quello gia' salvato
                      ...(d.costo_acquisto_unitario != null ? { costo_acquisto_unitario: d.costo_acquisto_unitario } : {})
                  };
                  return detail;
              })
          };
        await updateOrdine(id, ordineToSubmit);
        toast.success("Ordine aggiornato con successo!");
        setTimeout(() => {
          setSaving(false);
          setShowSaveProgress(false);
          navigate('/ordini');
        }, 500);
        } catch (error) {
          console.error("Errore nell'aggiornamento dell'ordine:", error.response || error);
          const dettaglio = error.response?.data?.detail;
          toast.error(typeof dettaglio === 'string' ? dettaglio : "Errore nell'aggiornamento dell'ordine.", { duration: 7000 });
          setSaving(false);
          setShowSaveProgress(false);
        }
              };

  // Apre l'anagrafica cliente completa; al salvataggio ClientiEdit torna qui (state.returnTo)
  const handleOpenClienteModal = () => {
    if (cliente) {
      navigate(`/clienti/edit/${cliente.id}`, { state: { returnTo: `/ordini/edit/${id}` } });
    }
  };

              return (
    <>
      <Container fluid>
        <Row className="align-items-center mb-3">
            <Col>
                <h2><FontAwesomeIcon icon={faFileInvoice} className="me-2" />Modifica Ordine #{id}</h2>
            </Col>
            <Col xs="auto" className="ms-auto">
                <Button variant="secondary" onClick={tornaIndietro} className="me-2">
                    Torna alla Lista
                </Button>
                <Button variant="success" className="me-2" onClick={() => {
                    const link = `https://www.modenacinema.it/account/ordini?valuta=${id}`;
                    const testo = `Ciao! Grazie per il tuo acquisto (ordine #${id}). Ti andrebbe di lasciarci una valutazione? Ci aiuteresti molto 🙏\n${link}`;
                    window.open(`https://wa.me/?text=${encodeURIComponent(testo)}`, '_blank');
                }}>
                    <FontAwesomeIcon icon={faEdit} className="me-2" />WhatsApp Valutazione
                </Button>
                <Button variant="info" onClick={handleResendConfirmationEmail} className="me-2">
                    Reinvia Email
                </Button>
                <Button variant="primary" type="submit" form="ordini-edit-form">
                    <FontAwesomeIcon icon={faEdit} className="me-2" />Salva Modifiche
                </Button>

            </Col>
        </Row>
        
        {cliente && (
            <Card className="mb-3 shadow-sm">
            <Card.Header className="bg-primary text-white">
                <FontAwesomeIcon icon={faFileInvoice} className="me-2" />
                Dati Cliente per Spedizione
            </Card.Header>
            <Card.Body>
                <Row>
                <Col md={6}>
                    <p className="mb-2">
                      <strong>Cliente:</strong>{' '}
                      <Button 
                        variant="link" 
                        className="p-0 text-decoration-none" 
                        style={{fontSize: 'inherit', verticalAlign: 'baseline'}}
                        onClick={handleOpenClienteModal}
                        title="Clicca per modificare"
                      >
                        {cliente.nome} {cliente.cognome} <FontAwesomeIcon icon={faEdit} size="sm" className="ms-1" />
                      </Button>
                    </p>
                    {cliente.denominazione_sociale && <p className="mb-2"><strong>Azienda:</strong> {cliente.denominazione_sociale}</p>}
                    <p className="mb-2"><strong>Indirizzo:</strong> {cliente.indirizzo}{cliente.numero_civico ? `, ${cliente.numero_civico}` : ''}, {cliente.cap}, {cliente.citta} ({cliente.provincia})</p>
                </Col>
                <Col md={6}>
                    <p className="mb-2"><strong>Email:</strong> <a href={`mailto:${ordine.email_spedizione || cliente.email}`}>{ordine.email_spedizione || cliente.email}</a></p>
                    <p className="mb-2"><strong>Telefono:</strong> {cliente.telefono}</p>
                    <p className="mb-2"><strong>Cellulare:</strong> {cliente.cellulare}</p>
                </Col>
                </Row>
            </Card.Body>
            </Card>
        )}

      <Form id="ordini-edit-form" onSubmit={handleSubmit}>
        <Tabs
          id="ordini-edit-tabs"
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-3"
        >
          {/* TAB INTESTAZIONE */}
          <Tab eventKey="intestazione" title={<><span className="me-2">📋</span>Dati Ordine</>}>
            <Card className="mb-3 shadow-sm">
              <Card.Header className="bg-light">Dati Generali Ordine</Card.Header>
              <Card.Body>
                <Row className="mb-3">
              <Form.Group as={Col} md={6} controlId="formOrigine">
                <Form.Label>Origine</Form.Label>
                <Form.Select
                  name="origine"
                  value={ordine.origine}
                  onChange={handleChange}
                >
                  <option value="">Seleziona Origine</option>
                  <option value="NEGOZIO">NEGOZIO</option>
                  <option value="SITO WEB">SITO WEB</option>
                  <option value="IBS">IBS</option>
                  <option value="FELTRINELLI">FELTRINELLI</option>
                  <option value="RIVERSAMENTI">RIVERSAMENTI</option>
                  <option value="ALTRO">ALTRO</option>
                </Form.Select>
              </Form.Group>
    
              { (ordine.origine === 'IBS' || ordine.origine === 'FELTRINELLI') && (
                <Form.Group as={Col} md={6} controlId="formNumeroOrdineMarketplace">
                  <Form.Label>Numero Ordine Marketplace</Form.Label>
                  <Form.Control
                    type="text"
                    name="numero_ordine_marketplace"
                    value={ordine.numero_ordine_marketplace}
                    onChange={handleChange}
                    placeholder="Inserisci numero ordine marketplace"
                  />
                </Form.Group>
              )}
    
              <Form.Group as={Col} md={6} controlId="formCliente">
                <Form.Label>Cliente</Form.Label>
                <Typeahead
                  id="cliente-typeahead"
                  labelKey={option => {
    const namePart = [option.nome, option.cognome].filter(Boolean).join(' ');
    const companyPart = option.denominazione_sociale ? ` (${option.denominazione_sociale})` : '';
    return (namePart + companyPart).trim() || 'Cliente Sconosciuto';
  }}
                  onChange={handleClienteChange}
                  options={allClienti}
                  placeholder="Seleziona un cliente..."
                  selected={ordine.cliente_id ? allClienti.filter(c => c.id === ordine.cliente_id) : []}
                  filterBy={['nome', 'cognome', 'denominazione_sociale']}
                />
              </Form.Group>
                  <Form.Group as={Col} md={4} controlId="formDataOrdine">
                    <Form.Label>Data Ordine</Form.Label>
                    <Form.Control type="date" name="data_ordine" value={ordine.data_ordine} onChange={handleOrdineChange} />
                  </Form.Group>
                </Row>
                <Row className="mb-3">
                  <Form.Group as={Col} md={4} controlId="formStatoOrdine">
                    <Form.Label>Stato Ordine</Form.Label>
                    <Form.Select name="stato" value={ordine.stato} onChange={handleOrdineChange}>
                      {statiOrdine.map(stato => (
                          <option key={stato} value={stato}>{stato}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  {ordine.stato === 'Rimborsato' && (
                    <Form.Group as={Col} md={8} className="mb-3">
                      <RimborsoMiraklInfo ordineId={ordine.id} />
                      <MotivoRimborsoFields
                        value={ordine}
                        onChange={(v) => setOrdine(prev => ({ ...prev, motivo_rimborso: v.motivo_rimborso, note_rimborso: v.note_rimborso }))}
                      />
                    </Form.Group>
                  )}
                  <Form.Group as={Col} md={8} controlId="formNoteOrdine">
                    <Form.Label>Note Ordine</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="note"
                      value={ordine.note || ''}
                      onChange={handleChange}
                      placeholder="Inserisci eventuali note sull'ordine..."
                    />
                  </Form.Group>
                </Row>
              </Card.Body>
            </Card>
          </Tab>

          {/* TAB PRODOTTI */}
          <Tab eventKey="prodotti" title={<><span className="me-2">📦</span>Prodotti ({ordine.dettagli.length})</>}>
            <Card className="mb-3 shadow-sm">
              <Card.Header className="bg-light">Dettagli Ordine</Card.Header>
              <Card.Body>
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Check
                      inline
                      type="radio"
                      label="Prodotto Esistente"
                      name="productType"
                      id="productTypeExisting"
                      checked={!isCustomProductMode}
                      onChange={() => setIsCustomProductMode(false)}
                    />
                    <Form.Check
                      inline
                      type="radio"
                      label="Prodotto Personalizzato"
                      name="productType"
                      id="productTypeCustom"
                      checked={isCustomProductMode}
                      onChange={() => setIsCustomProductMode(true)}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {!isCustomProductMode ? (
                <>
                  <Row className="align-items-end mb-3">
                    <Form.Group as={Col} md={7} controlId="formItemSearch">
                      <Form.Label>Aggiungi Prodotto/Servizio</Form.Label>
                      <Typeahead
                        id="item-search-typeahead"
                        labelKey={(option) => {
                            let label = '';
                            if (option.item_type === "Film" || option.item_type === "Musica") {
                                label = `${option.titolo} - ${option.supporto || option.item_type}${option.sku ? ` - SKU: ${option.sku}` : ''} - Qt: ${option.quantita || 0}`;
                            } else if (option.item_type === "Servizio") {
                              label = `${option.titolo} - Servizio${option.sku ? ` - SKU: ${option.sku}` : ''} - Prezzo: ${option.prezzo}`;
                            } else {
                              label = option.titolo; // Fallback
                            }
                            return label;
                        }}
                        options={searchResults}
                        onInputChange={handleSearch}
                        onChange={(selected) => {
                          if (selected.length > 0) {
                            setSelectedItem(selected[0]);
                          } else {
                            setSelectedItem(null);
                          }
                        }}
                        placeholder="Cerca o seleziona un prodotto/servizio..."
                        selected={selectedItem ? [selectedItem] : []}
                        filterBy={() => true} // Disable client-side filtering
                        renderMenuItemChildren={(option, props) => {
                          const isOutOfStock = (option.item_type === "Film" || option.item_type === "Musica") && option.quantita === 0;
                          let label = '';
                          if (option.item_type === "Film" || option.item_type === "Musica") {
                            label = `${option.titolo} - ${option.supporto || option.item_type}${option.sku ? ` - SKU: ${option.sku}` : ''} - Qt: ${option.quantita || 0}`;
                          } else if (option.item_type === "Servizio") {
                            label = `${option.titolo} - Servizio${option.sku ? ` - SKU: ${option.sku}` : ''} - Prezzo: ${option.prezzo}`;
                          } else {
                            label = option.titolo; // Fallback
                          }

                          return (
                            <div style={{ color: isOutOfStock ? 'red' : 'black' }}>
                              {label}
                            </div>
                          );
                        }}
                      />
                    </Form.Group>
                    <Col md={2}> {/* Explicit Col wrapper */}
                      <Form.Group controlId="formQuantita">
                        <Form.Label>Quantità</Form.Label>
                        <Form.Control type="number" value={quantita} onChange={(e) => setQuantita(parseInt(e.target.value))} min="1" />
                      </Form.Group>
                    </Col>
                    <Col md={1} className="d-flex align-items-end">
                      <Button variant="secondary" onClick={addLineItem} className="w-100">Aggiungi</Button>
                    </Col>
                  </Row>
                </>
              ) : (
                <Row className="align-items-end mb-3">
                  <Form.Group as={Col} md={4} controlId="formCustomProductDescription">
                    <Form.Label>Descrizione Prodotto</Form.Label>
                    <Form.Control
                      type="text"
                      value={customProductDescription}
                      onChange={(e) => setCustomProductDescription(e.target.value)}
                      placeholder="Descrizione prodotto personalizzato"
                    />
                  </Form.Group>
                  <Form.Group as={Col} md={2} controlId="formCustomProductPrice">
                    <Form.Label>Prezzo Unitario</Form.Label>
                    <Form.Control
                      type="number"
                      value={customProductPrice}
                      onChange={(e) => setCustomProductPrice(parseFloat(e.target.value))}
                      step="0.01"
                      min="0"
                    />
                  </Form.Group>
                  <Form.Group as={Col} md={2} controlId="formCustomProductCost">
                    <Form.Label>Costo Unitario</Form.Label>
                    <Form.Control
                      type="number"
                      value={customProductCost}
                      onChange={(e) => setCustomProductCost(parseFloat(e.target.value))}
                      step="0.01"
                      min="0"
                    />
                  </Form.Group>
                  <Form.Group as={Col} md={2} controlId="formCustomProductIva">
                    <Form.Label>IVA %</Form.Label>
                    <Form.Control
                      type="number"
                      value={customProductIva}
                      onChange={(e) => setCustomProductIva(parseFloat(e.target.value))}
                      step="0.01"
                      min="0"
                    />
                  </Form.Group>
                  <Form.Group as={Col} md={1} controlId="formQuantitaCustom">
                    <Form.Label>Quantità</Form.Label>
                    <Form.Control
                      type="number"
                      value={quantita}
                      onChange={(e) => setQuantita(parseInt(e.target.value))}
                      min="1"
                    />
                  </Form.Group>
                  <Col md={1} className="d-flex align-items-end">
                    <Button variant="secondary" onClick={addLineItem} className="w-100">Aggiungi</Button>
                  </Col>
                </Row>
              )}

               <Table striped bordered hover responsive className="align-middle">
                 <thead className="table-light">
                    <tr>
                        <th style={{width: '50px'}}>Evadi</th>
                        <th>Prodotto / Descrizione</th>
                        <th style={{width: '80px'}}>Q.tà</th>
                        <th style={{width: '120px'}}>Prezzo Unit.</th>
                        <th style={{width: '80px'}}>IVA %</th>
                        <th style={{width: '120px'}}>Subtotale</th>
                        <th style={{width: '50px'}}>Azioni</th>
                    </tr>
                </thead>
                 <tbody>
                     {ordine.dettagli.map((item, index) => {
                         const prezzo = item.prezzo_unitario_libero || item.prezzo_unitario || 0;
                         const costo = item.costo_unitario_libero || item.costo_acquisto_unitario || 0;
                         const sub = prezzo * item.quantita;
                         const margineLordo = sub - (costo * item.quantita);
                         const marginePercentuale = sub > 0 ? (margineLordo / sub) * 100 : 0;
                         const stockClass = (item.item_type === "Film" || item.item_type === "Musica") && item.quantita_magazzino !== undefined && item.quantita_magazzino === 0 ? 'table-danger' : '';
                         
                         // Determina descrizione e supporto
                         let descrizione = item.prodotto_libero ? item.descrizione_prodotto_libero : item.titolo;
                         let supporto = '';
                         let ean = '';
                         let ubicazione = '';
                         let linkScheda = null;
                         if (item.film && item.film.supporto) {
                           supporto = item.film.supporto;
                         } else if (item.musica && item.musica.supporto) {
                           supporto = item.musica.supporto;
                         } else if (item.collectible) {
                           // Per collectibles, mostra la categoria_prodotto come supporto
                           supporto = item.collectible.categoria_prodotto || "Collectible";
                         } else if (item.item_type === "Servizio") {
                           supporto = "Servizio";
                         }
                         if (item.film) {
                           ean = item.film.barcode_ibs || '';
                           ubicazione = item.ubicazione_scelta || item.film.ubicazione || '';
                           linkScheda = `/film/${item.film_id}`;
                         } else if (item.musica) {
                           ean = item.musica.barcode_ibs || '';
                           ubicazione = item.ubicazione_scelta || item.musica.ubicazione || '';
                           linkScheda = `/musica/${item.musica_id}`;
                         }

                         const isEditing = editingDetail === index;
                         
                         return (
                           <tr key={item.id || `custom-${index}`} className={stockClass}>
                             <td className="text-center">
                                <Form.Check 
                                    type="checkbox"
                                    id={`detail-check-${item.id}`}
                                    checked={selectedDetails.has(item.id)}
                                    onChange={() => handleDetailSelection(item.id)}
                                    disabled={!item.id}
                                />
                             </td>
                             <td>
                               {isEditing ? (
                                 <Form.Control
                                   type="text"
                                   value={descrizione}
                                   onChange={(e) => handleDescriptionChange(index, e.target.value)}
                                   size="sm"
                                 />
                               ) : (
                                 <div>
                                   {linkScheda ? (
                                     <Link to={linkScheda} className="text-decoration-none">
                                       <strong>{descrizione}</strong>
                                     </Link>
                                   ) : (
                                     <strong>{descrizione}</strong>
                                   )}
                                   {supporto && <><br/><Badge bg="secondary" className="mt-1">{supporto}</Badge></>}
                                   {stockClass && <><br/><Badge bg="danger" className="mt-1">Esaurito</Badge></>}
                                   {(ean || ubicazione) && (
                                     <div className="small text-muted mt-1">
                                       {ean && <>EAN: {ean}</>}
                                       {ean && ubicazione && ' · '}
                                       {ubicazione && <>Ubicazione: {ubicazione}</>}
                                     </div>
                                   )}
                                 </div>
                               )}
                             </td>
                             <td>
                                <Form.Control
                                    type="number"
                                    value={item.quantita}
                                    onChange={(e) => handleQuantityChange(index, parseInt(e.target.value))}
                                    min="1"
                                    size="sm"
                                />
                             </td>
                             <td>
                               {isEditing ? (
                                 <Form.Control
                                   type="number"
                                   value={prezzo}
                                   onChange={(e) => handlePriceChange(index, e.target.value)}
                                   step="0.01"
                                   size="sm"
                                 />
                               ) : (
                                 `€ ${prezzo.toFixed(2)}`
                               )}
                             </td>
                             <td className="text-center">{(item.iva_percentuale_libero || item.iva_percentuale || 0)}%</td>
                             <td className="text-end"><strong>€ {sub.toFixed(2)}</strong></td>
                             <td>
                               <div className="d-flex gap-1">
                                 <Button 
                                   variant={isEditing ? "success" : "outline-primary"} 
                                   size="sm" 
                                   onClick={() => toggleEditDetail(index)}
                                   title={isEditing ? "Conferma" : "Modifica descrizione/prezzo"}
                                 >
                                   <FontAwesomeIcon icon={faEdit} />
                                 </Button>
                                 <Button 
                                   variant="outline-danger" 
                                   size="sm" 
                                   onClick={() => removeLineItem(index)}
                                   title="Rimuovi"
                                 >
                                   ×
                                 </Button>
                               </div>
                             </td>
                           </tr>
                         );
                     })}
                 </tbody>
               </Table>
               {id && (
                   <Button 
                       variant="success" 
                       onClick={handlePartialEvasion} 
                       disabled={selectedDetails.size === 0}
                       className="mt-2"
                   >
                       Evadi Selezionati e Crea Residuo
                   </Button>
               )}
             </Card.Body>
           </Card>
          </Tab>

          {/* TAB COSTI E MARGINALITÀ */}
          <Tab eventKey="costi" title={<><span className="me-2">💰</span>Costi e Marginalità</>}>
            <Card className="mb-3 shadow-sm">
              <Card.Header className="bg-light">Pagamento</Card.Header>
              <Card.Body>
                <Row className="mb-3">
                  <Form.Group as={Col} md={6} controlId="formMetodoPagamento">
                    <Form.Label>Metodo di pagamento <span className="text-muted">(come ha pagato il cliente)</span></Form.Label>
                    <Form.Select name="payment_method" value={ordine.payment_method || ''} onChange={handlePaymentMethodChange}>
                      <option value="">Seleziona…</option>
                      {METODI_PAGAMENTO.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                      {ordine.payment_method && !METODI_PAGAMENTO.some(m => m.value === ordine.payment_method) && (
                        <option value={ordine.payment_method}>{labelMetodoPagamento(ordine.payment_method)}</option>
                      )}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group as={Col} md={6} controlId="formBanca">
                    <Form.Label>Banca <span className="text-muted">(rapporto di incasso: dove sono arrivati i soldi)</span></Form.Label>
                    <Form.Select name="banca_id" value={ordine.banca_id || ''} onChange={handleBancaChange}>
                      <option value="">Seleziona…</option>
                      {banche.map(b => <option key={b.id} value={b.id}>{b.denominazione}</option>)}
                    </Form.Select>
                  </Form.Group>
                </Row>
              </Card.Body>
            </Card>

            <Card className="mb-3 shadow-sm">
              <Card.Header className="bg-light">Spese e Sconti</Card.Header>
              <Card.Body>
                <Row className="mb-3">
                  <Form.Group as={Col} md={4} controlId="formSpeseTrasporto">
                    <Form.Label>Spese Trasporto</Form.Label>
                    <Form.Control type="number" value={(ordine.spese_trasporto || 0).toFixed(2)} readOnly disabled />
                  </Form.Group>
                  <Form.Group as={Col} md={4} controlId="formSpeseIncasso">
                    <Form.Label>Spese Incasso %</Form.Label>
                    <Form.Control type="number" name="spese_incasso_percentuale" value={ordine.spese_incasso_percentuale} onChange={handleOrdineChange} step="0.1" />
                  </Form.Group>
                  <Form.Group as={Col} md={4} controlId="formAcconto">
                    <Form.Label>Acconto</Form.Label>
                    <Form.Control type="number" name="acconto" value={ordine.acconto} onChange={handleOrdineChange} step="0.01" />
                  </Form.Group>
                </Row>
                <Row className="mb-3 align-items-end">
                  <Form.Group as={Col} md={4} controlId="formCostoContrassegno">
                    <Form.Label>Costo contrassegno</Form.Label>
                    <Form.Control type="number" name="costo_contrassegno" value={ordine.costo_contrassegno} onChange={handleOrdineChange} step="0.01" min="0" />
                  </Form.Group>
                  <Col md={8}>
                    {id && (
                      <Button variant="outline-primary" onClick={handleRicalcolaPromo} title="Applica le promozioni e la spedizione del sito agli articoli attuali">
                        Ricalcola promo e spedizione del sito
                      </Button>
                    )}
                  </Col>
                </Row>
                <Row>
                  <Form.Group as={Col} md={6} controlId="formSconto">
                    <Form.Label>Sconto</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="number"
                        name="sconto"
                        value={ordine.sconto}
                        onChange={handleChange}
                        step="0.01"
                      />
                      <Form.Select
                        name="tipo_sconto"
                        value={ordine.tipo_sconto}
                        onChange={handleChange}
                        style={{ width: 'auto' }}
                      >
                        <option value="fisso">€</option>
                        <option value="percentuale">%</option>
                      </Form.Select>
                    </InputGroup>
                  </Form.Group>
                </Row>
              </Card.Body>
            </Card>

            <Card className="mb-3 shadow-sm">
              <Card.Header className="bg-success text-white">Totali Ordine</Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <p className="mb-2"><strong>Subtotale Imponibile:</strong> € {(ordine.subtotale_imponibile || 0).toFixed(2)}</p>
                    <p className="mb-2"><strong>Totale IVA:</strong> € {(ordine.totale_iva || 0).toFixed(2)}</p>
                  </Col>
                  <Col md={6} className="text-end">
                    <h3 className="text-success"><strong>Totale Finale: € {(ordine.totale_ordine || 0).toFixed(2)}</strong></h3>
                    {parseFloat(ordine.acconto || 0) > 0 && (
                      <p className="mb-0 text-muted">Acconto € {parseFloat(ordine.acconto).toFixed(2)} — da incassare € {((ordine.totale_ordine || 0) - parseFloat(ordine.acconto)).toFixed(2)}</p>
                    )}
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="mb-3 shadow-sm">
              <Card.Header className="bg-warning">
                <FontAwesomeIcon icon={faChartLine} className="me-2" />
                Marginalità
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <p className="mb-2"><strong>Costo Prodotti:</strong> € {(ordine.costo_prodotti || 0).toFixed(2)}</p>
                    <p className="mb-2"><strong>Costo Marketplace:</strong> € {(ordine.costo_marketplace || 0).toFixed(2)}</p>
                    <p className="mb-2"><strong>Costo Corrieri:</strong> € {(ordine.costo_reale_corriere || 0).toFixed(2)}</p>
                  </Col>
                  <Col md={6} className="text-end">
                    <h4 className="text-success">Margine Lordo: € {(ordine.margine_lordo || 0).toFixed(2)}</h4>
                    <h4 className="text-primary">Margine %: {(ordine.margine || 0).toFixed(2)}%</h4>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Tab>

          {/* TAB SPEDIZIONE */}
          <Tab eventKey="spedizione" title={<><span className="me-2">🚚</span>Spedizione</>}>
            <Card className="mb-3 shadow-sm">
              <Card.Header className="bg-light">Dati Spedizione</Card.Header>
              <Card.Body>
                <Row className="mb-3">
                  <Form.Group as={Col} md={6} controlId="formCorriere">
                    <Form.Label>Corriere</Form.Label>
                    <Form.Select name="corriere_id" value={ordine.corriere_id} onChange={handleOrdineChange}>
                        <option value="">Seleziona un tipo di spedizione...</option>
                        {corrieri.filter(c => c.tipo_spedizione).map(c => <option key={c.id} value={c.id}>{c.tipo_spedizione}</option>)}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group as={Col} md={6} controlId="formTrackingCode">
                    <Form.Label>Codice di Tracking</Form.Label>
                    <Form.Control
                        type="text"
                        name="tracking_code"
                        value={ordine.tracking_code || ''}
                        onChange={handleChange}
                        placeholder="Inserisci il codice di tracking"
                    />
                  </Form.Group>
                </Row>
              </Card.Body>
            </Card>

          </Tab>

          {/* TAB FIREFLY III */}
          <Tab eventKey="firefly" title={<><span className="me-2">📈</span>Firefly III</>}>
            <Card className="mb-3 shadow-sm">
              <Card.Header className="bg-light">Integrazione Firefly III</Card.Header>
              <Card.Body>
                {ordine.stato === 'Concluso' ? (
                  <>
                    {[
                      // Revenue
                      { field: 'firefly_ricavo_film_nuovo_transaction_id', label: 'Ricavo Film Nuovo' },
                      { field: 'firefly_ricavo_film_usato_transaction_id', label: 'Ricavo Film Usato' },
                      { field: 'firefly_ricavo_musica_nuovo_transaction_id', label: 'Ricavo Musica Nuova' },
                      { field: 'firefly_ricavo_musica_usato_transaction_id', label: 'Ricavo Musica Usata' },
                      { field: 'firefly_ricavo_collezionabili_transaction_id', label: 'Ricavo Collezionabili' },
                      { field: 'firefly_ricavo_servizi_transaction_id', label: 'Ricavo Servizi' },
                      { field: 'firefly_ricavo_extra_transaction_id', label: 'Ricavo Extra' },
                      { field: 'firefly_ricavo_spedizione_transaction_id', label: 'Incasso Spedizione' },
                      { field: 'firefly_ricavo_iva_transaction_id', label: 'IVA Incassata' },
                      
                      // Costs
                  { field: 'firefly_costo_prodotti_transaction_id', label: 'Costo Prodotti (COGS)' },
                  { field: 'firefly_costo_servizi_transaction_id', label: 'Costo Servizi (COGS)' },
                  { field: 'firefly_costo_corriere_transaction_id', label: 'Costo Corriere' },
                  { field: 'firefly_costo_marketplace_transaction_id', label: 'Costo Marketplace' },
                  { field: 'firefly_costo_bancario_transaction_id', label: 'Costo Bancario' },
                  { field: 'firefly_costo_commissioni_marketplace_transaction_id', label: 'Commissioni Marketplace' },
                ]
                .filter(({ field }) => ordine[field]) // Mostra solo le transazioni con un ID
                .map(({ field, label }) => (
                  <p key={field}>
                    {label} ID: 
                    <a href={`http://localhost:8080/transactions/show/${ordine[field]}`} target="_blank" rel="noopener noreferrer">
                      {ordine[field]}
                    </a>
                  </p>
                ))
                }
              </>
            ) : (
              <p>La sincronizzazione con Firefly III avverrà quando lo stato dell'ordine sarà "Concluso".</p>
            )}
          </Card.Body>
        </Card>
          </Tab>

          {/* TAB MESSAGGI IBS */}
          <Tab eventKey="messaggi" title={<><span className="me-2">💬</span>Messaggi IBS <Badge bg={MESSAGGI_BADGE_BG[ordine.stato_messaggi] || 'secondary'} className="ms-1">{ordine.stato_messaggi}</Badge></>}>
            <Card className="mb-3 shadow-sm">
              <Card.Header className="bg-light">Messaggi IBS</Card.Header>
              <Card.Body style={{ height: '500px', padding: 0 }}>
                <OrderMessages ordineId={ordine.id} />
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>
      </Form>
    </Container>

    {/* Modal Progress Bar per salvataggio */}
    <Modal 
      show={showSaveProgress} 
      backdrop="static" 
      keyboard={false}
      centered
    >
      <Modal.Header>
        <Modal.Title>
          <Spinner animation="border" size="sm" className="me-2" />
          Salvataggio in corso...
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>Aggiornamento dell'ordine #{id} in corso. Attendere...</p>
        <ProgressBar animated now={100} variant="primary" />
      </Modal.Body>
    </Modal>

  </>
  );
};

export default OrdiniEdit;
